import { Button, TextField, Typography } from '@mui/material';
import { OVERGAE_SETTINGS, PAYMENT_PROVIDERS, PaymentProviderSchema } from '@weft/types';
import { CURRENCIES } from '@weft/types';
import type { Currency, OverageSettings } from '@weft/types';
import type { NewCustomer } from '@weft/types';
import { isNullOrUndefined, raiseUnreachable } from '@weft/utils';
import { vyFormRegister } from 'apps/app/src/utils/react-hook-forms-utils';
import cn from 'classnames';
import React, { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useDropdownChangeHandler } from '../../../../components/design/dropdowns/Dropdown';
import { StripeCustomerDialogForm } from '../../Integrations/StripeCustomersDialog';
import { INVOICE_DUE_DATE_VALUES } from '../create-customer-page-consts/invoice-due-date-consts';
import { CreateCustomerSettingsSelect } from './CreateCustomerSettingsSelect';

export const CreateCustomerSettings: React.FC = () => {
  const [showStripeCustomersDialog, setShowStripeCustomersDialog] = useState(false);
  const { register, getFieldState, getValues, watch, setValue, resetField } = useFormContext<NewCustomer>();
  const handleSelectChange = useDropdownChangeHandler<NewCustomer>();
  const hasTaxError = getFieldState('settings.taxRate').invalid;
  watch('settings');
  const { ref: currencyRef, ...currencyRegister } = register('settings.currency');
  const { ref: overageRef, ...overageRegister } = register('settings.overage');
  const { ref: _ref, ...taxRegister } = vyFormRegister(register, 'settings.taxRate', {
    valueAsNumber: true,
  });

  const { ref: paymentProviderRef, ...paymentProviderRegister } = register('settings.paymentProvider');
  const { ref: invoiceDueDateRef, ...paymentDueDateRegister } = register('settings.paymentDueDays');

  return (
    <div className='border-l-disabled laptop:px-6 flex justify-center border-l bg-[#ffffff] pt-12'>
      <div className="laptop:px-0 space-y-6 px-8">
        <Typography variant='h6'>Customer Settings</Typography>
        <div className='space-y-3'></div>

        <CreateCustomerSettingsSelect
          title='Currency'
          items={Object.values(CURRENCIES) as Currency[]}
          defaultValue={getValues('settings.currency')}
          {...currencyRegister}
          onChange={handleSelectChange('settings.currency')}
        />
        <div className={cn(
          'flex h-16 items-center justify-between rounded-2lg border bg-white p-5 laptop:w-72',
          hasTaxError && 'border-input-error',
          !hasTaxError && 'border-line',
        )}
        >
          <Typography>Tax</Typography>
          <div className="w-min flex-row pr-0">
            <TextField
              size='small'
              placeholder="Tax %"
              className="w-32 px-2 py-1.5"
              type="number"
              {...taxRegister}
              value={getValues('settings.taxRate')}
              onChange={(e) => setValue('settings.taxRate', Number(e.target.value) ?? 0)}
            />
          </div>
        </div>
        <CreateCustomerSettingsSelect
          title='Payment provider'
          items={['None', ...Object.values(PAYMENT_PROVIDERS)]}
          defaultValue={getValues('settings.paymentProvider')}
          {...paymentProviderRegister}
          onChange={(value) => {
            if (isNullOrUndefined(value) || value === 'None') {
              resetField('settings.paymentProvider', { keepDirty: true });
            }
            const parsedValue = PaymentProviderSchema.parse(value);
            setValue('settings.paymentProvider', parsedValue, { shouldDirty: true });
          }}
        />
        {getValues('settings.paymentProvider') === PAYMENT_PROVIDERS.Stripe && (
          <>
            <button
              className={cn(
                'flex h-16 w-full items-center justify-between rounded-2lg border bg-white p-5 cursor-pointer',
                hasTaxError && 'border-input-error',
                !hasTaxError && 'border-line',
              )}
              onClick={() => {
                setShowStripeCustomersDialog(true);
              }}
            >
              <span className="text-filled-text">Choose stripe customer</span>
              <span>{getValues('settings.paymentProviderMetadata.providerExternalId')?.slice(0, 7)}</span>
            </button>
            <StripeCustomerDialogForm
              open={showStripeCustomersDialog}
              onChoose={(customerId) => setValue('settings.paymentProviderMetadata.providerExternalId', customerId)}
              onClose={() => {
                setShowStripeCustomersDialog(false);
              }}
            />
          </>
        )}
        <CreateCustomerSettingsSelect
          title='Invoice due date'
          items={Object.values(INVOICE_DUE_DATE_VALUES) as number[]}
          defaultValue={getValues('settings.paymentDueDays')}
          parseItemToString={item => `Net ${item.toString()}`}
          {...paymentDueDateRegister}
          onChange={handleSelectChange('settings.paymentDueDays')}
        />
        <CreateCustomerSettingsSelect
          title='Charge Overage'
          items={Object.values(OVERGAE_SETTINGS) as OverageSettings[]}
          defaultValue={getValues('settings.overage')}
          parseItemToString={item => getOverageSettingDescription(item)}
          {...overageRegister}
          onChange={handleSelectChange('settings.overage')}
        />
        <Button
          variant="contained"
          size="large"
          type="submit"
          style={{ margin: '0 auto', display: 'flex' }}
        >
          Save
        </Button>
      </div>
    </div>
  );
};

export function getOverageSettingDescription(setting: OverageSettings) {
  switch (setting) {
    case OVERGAE_SETTINGS.NoCharge:
      return 'Never charge';
    case OVERGAE_SETTINGS.ChargeTier:
      return 'Always charge';
    case OVERGAE_SETTINGS.ChargeExtras:
      return 'Charge separately at end date';
    default:
      return raiseUnreachable(setting);
  }
}